import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../config/supabase';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { decryptApiKey } from '../utils/encryption';

const GeminiContext = createContext();

// ตัวแปรสถานะแบบ global สำหรับการจำกัดอัตราการส่งคำขอ
// ใช้ตัวแปรนอก component เพื่อให้ไม่ถูก reset เมื่อ component re-render
let geminiFreeRequestCount = 0;
let geminiFreeIsRateLimited = false;
let geminiFreeRateLimitTimer = null;
let geminiFreeRateLimitPromise = Promise.resolve(); // เริ่มต้นด้วย Promise ที่เสร็จสิ้นแล้ว

export const GeminiProvider = ({ children }) => {
  const { user } = useAuth();
  const [gemini, setGemini] = useState(null);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ตัวแปรสำหรับ Gemini Free ใน component เพื่อใช้ในการแสดงผลใน UI
  const [requestCount, setRequestCount] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitTimer, setRateLimitTimer] = useState(null);
  
  // อัปเดตตัวแปรใน component เมื่อตัวแปร global เปลี่ยนแปลง
  useEffect(() => {
    const updateState = () => {
      setRequestCount(geminiFreeRequestCount);
      setIsRateLimited(geminiFreeIsRateLimited);
    };
    
    // อัปเดตทุก 1 วินาที
    const interval = setInterval(updateState, 1000);
    
    // อัปเดตทันทีตอนเริ่มต้น
    updateState();
    
    return () => clearInterval(interval);
  }, []);

  // เรียกใช้ initializeGemini ครั้งแรกเมื่อโหลดหน้า
  // เพื่อให้ระบบทำงานได้กับ API key ที่มีอยู่แล้ว
  useEffect(() => {
    if (user) {
      initializeGemini();
    }
  }, [user]);

  const initializeGemini = async () => {
    try {
      setLoading(true);
      setError(null);

      // ดึง API key จากฐานข้อมูล
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select(`
          gemini (
            api_key,
            is_active
          )
        `)
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const encryptedApiKey = data?.gemini?.[0]?.api_key;
      const isActive = data?.gemini?.[0]?.is_active;

      if (!encryptedApiKey || !isActive) {
        throw new Error('กรุณาตั้งค่า Gemini API Key ใน<a href="/profile">หน้าโปรไฟล์</a>');
      }
      
      // ถอดรหัส API key ก่อนใช้งาน
      const apiKey = decryptApiKey(encryptedApiKey);

      // ตรวจสอบรูปแบบของ API key คร่าวๆ
      if (!apiKey.startsWith('AI') && !apiKey.startsWith('ai')) {
        throw new Error('รูปแบบ Gemini API Key ไม่ถูกต้อง API key ควรขึ้นต้นด้วย "AI" กรุณาแก้ไขใน<a href="/profile">หน้าโปรไฟล์</a>');
      }

      try {
        // สร้าง Gemini instance ด้วย API key ที่ถอดรหัสแล้ว
        const genAI = new GoogleGenerativeAI(apiKey);
        const geminiModel = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
        
        // ทดสอบการเชื่อมต่อด้วยการส่งคำของ่ายๆ
        await geminiModel.generateContent("test");
        
        setGemini(genAI);
        setModel(geminiModel);
      } catch (apiError) {
        console.error('Error testing Gemini API:', apiError);
        if (apiError.message.includes('API key')) {
          throw new Error('Gemini API key ไม่ถูกต้อง กรุณาตรวจสอบและแก้ไขใน<a href="/profile">หน้าโปรไฟล์</a>');
        } else {
          throw new Error('ไม่สามารถเชื่อมต่อกับ Gemini API: ' + apiError.message);
        }
      }
    } catch (err) {
      console.error('Error initializing Gemini:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันคำนวณค่าใช้จ่าย
  const calculateCost = (usage) => {
    const input_tokens = usage.input_tokens || 0;
    const output_tokens = usage.output_tokens || 0;
    const total_tokens = input_tokens + output_tokens;

    // คำนวณราคา (Gemini-2.0-flash rates)
    const input_cost = (input_tokens / 1000) * 0.00035;  // $0.00035 per 1K input tokens
    const output_cost = (output_tokens / 1000) * 0.00105;  // $0.00105 per 1K output tokens
    const total_cost_usd = input_cost + output_cost;
    const total_cost_thb = total_cost_usd * 35.5;  // อัตราแลกเปลี่ยนโดยประมาณ

    return {
      input_tokens,
      output_tokens,
      total_tokens,
      input_cost_usd: input_cost,
      output_cost_usd: output_cost,
      total_cost_usd: total_cost_usd,
      total_cost_thb: total_cost_thb
    };
  };

  // ฟังก์ชันสำหรับจัดการการจำกัดอัตราการส่งคำขอสำหรับ Gemini Free
  const checkRateLimit = async () => {
    // รอให้ Promise ก่อนหน้าเสร็จสิ้นก่อน
    await geminiFreeRateLimitPromise;
    
    // ถ้ากำลังถูกจำกัดอัตรา ให้รอจนกว่าจะพ้นระยะเวลาจำกัด
    if (geminiFreeIsRateLimited) {
      throw new Error('คุณใช้งาน Gemini Free เกินขีดจำกัด กรุณารอ 1 นาทีแล้วลองใหม่อีกครั้ง');
    }
    
    // สร้าง Promise ใหม่สำหรับการจำกัดอัตราครั้งนี้
    geminiFreeRateLimitPromise = new Promise((resolve) => {
      // เพิ่มจำนวนคำขอ
      geminiFreeRequestCount++;
      console.log(`Gemini Free request count: ${geminiFreeRequestCount}/15`);
      
      if (geminiFreeRequestCount >= 15) {
        console.log('Rate limit reached, pausing for 1 minute');
        geminiFreeIsRateLimited = true;
        
        // ตั้งเวลาพัก 1 นาที
        geminiFreeRateLimitTimer = setTimeout(() => {
          console.log('Rate limit reset');
          geminiFreeIsRateLimited = false;
          geminiFreeRequestCount = 0;
          resolve(); // เรียก resolve เมื่อพ้นขีดจำกัดแล้ว
        }, 60000); // 60 วินาที = 1 นาที
      } else {
        // ถ้ายังไม่ถึงขีดจำกัด ให้ resolve ทันที
        resolve();
      }
    });
    
    // รอให้ Promise เสร็จสิ้น
    await geminiFreeRateLimitPromise;
  };
  
  // เคลียร์ไทเมอร์เมื่อ component unmount
  useEffect(() => {
    return () => {
      if (rateLimitTimer) {
        clearTimeout(rateLimitTimer);
      }
    };
  }, [rateLimitTimer]);

  // ฟังก์ชันสำหรับสร้าง prompt สำหรับ Gemini ปกติ
  const generatePrompt = async (modelType, keyword, amount = 1) => {
    // ถ้ายังไม่ได้เริ่มต้น Gemini ให้เริ่มต้นก่อน
    if (!model) {
      try {
        await initializeGemini();
      } catch (error) {
        throw new Error('Gemini ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
      }
    }
    
    // ตรวจสอบอีกครั้งหลังจากเริ่มต้น
    if (!model) {
      throw new Error('Gemini ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
    }

    try {
      // เลือก system prompt ตามโมเดล
      let systemPrompt = '';
      if (modelType === 'Polar Bear') {
        systemPrompt = `You are a commercial stock photo keyword generator.
Your task is to generate keywords for the given input word.
Each keyword should be 2-3 words long and related to stock photography.

Rules:
- Generate EXACTLY ${amount} keyword(s), no more no less
- Only respond with keywords, no explanations
- Separate keywords with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "dog"
Example output: "happy puppy"

Example input with amount=3: "dog"
Example output: "happy puppy | playful canine | loyal companion"

Do not explain or add any other text. Just return the keywords.`;
      } else if (modelType === 'Chicken') {
        systemPrompt = `Create ${amount} detailed stock photo prompt(s) (max 120 chars each).
Must be related to the input keyword.
Include: main subject, action/pose, mood, lighting, camera angle, setting.
Make it artistic but keep commercial value.

Rules:
- Generate EXACTLY ${amount} prompt(s), no more no less
- Separate prompts with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot"

Example input with amount=2: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot | Elegant dog savoring premium kibble, modern kitchen setting, warm backlighting, low angle perspective"

Do not explain or add any other text. Just return the prompts separated by |.`;
      }

      const result = await model.generateContent([
        systemPrompt,
        keyword
      ]);
      
      const response = await result.response;
      const text = response.text();
      
      // คำนวณค่าใช้จ่าย (ประมาณการเนื่องจาก Gemini API อาจไม่ส่งค่า token usage มา)
      const usage = {
        input_tokens: (systemPrompt.length + keyword.length) / 4, // ประมาณการ
        output_tokens: text.length / 4 // ประมาณการ
      };
      const cost = calculateCost(usage);

      return {
        content: text,
        cost
      };
    } catch (err) {
      console.error('Error generating prompt with Gemini:', err);
      
      // ตรวจสอบประเภทของข้อผิดพลาดเพื่อแสดงข้อความที่ชัดเจน
      if (err.message.includes('API key')) {
        throw new Error('API key ของ Gemini ไม่ถูกต้อง กรุณาตรวจสอบและแก้ไขในหน้าโปรไฟล์');
      } else if (err.message.includes('network') || err.message.includes('Network')) {
        throw new Error('เกิดปัญหาการเชื่อมต่อกับ Gemini API กรุณาลองใหม่อีกครั้ง');
      } else if (err.message.includes('quota') || err.message.includes('limit')) {
        throw new Error('คุณใช้ Gemini API เกินโควต้าที่กำหนด กรุณาตรวจสอบการใช้งานหรือเปลี่ยน API key');
      } else {
        throw new Error('ไม่สามารถสร้าง prompt ด้วย Gemini ได้: ' + err.message);
      }
    }
  };



  // ฟังก์ชันสำหรับสร้าง prompt สำหรับ Gemini Free
  const generatePromptFree = async (modelType, keyword, amount = 1) => {
    try {
      // เพิ่มจำนวนคำขอและตรวจสอบการจำกัดอัตรา
      await checkRateLimit();
      
      // ถ้ายังไม่ได้เริ่มต้น Gemini ให้เริ่มต้นก่อน
      if (!model) {
        try {
          await initializeGemini();
        } catch (error) {
          throw new Error('Gemini ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
        }
      }
      
      // ตรวจสอบอีกครั้งหลังจากเริ่มต้น
      if (!model) {
        throw new Error('Gemini ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
      }
      // ใช้โค้ดเดียวกับ generatePrompt แต่มีการตรวจสอบการจำกัดอัตราการส่งคำขอ
      // เลือก system prompt ตามโมเดล
      let systemPrompt = '';
      if (modelType === 'Polar Bear') {
        systemPrompt = `You are a commercial stock photo keyword generator.
Your task is to generate keywords for the given input word.
Each keyword should be 2-3 words long and related to stock photography.

Rules:
- Generate EXACTLY ${amount} keyword(s), no more no less
- Only respond with keywords, no explanations
- Separate keywords with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "dog"
Example output: "happy puppy"

Example input with amount=3: "dog"
Example output: "happy puppy | playful canine | loyal companion"

Do not explain or add any other text. Just return the keywords.`;
      } else if (modelType === 'Chicken') {
        systemPrompt = `Create ${amount} detailed stock photo prompt(s) (max 120 chars each).
Must be related to the input keyword.
Include: main subject, action/pose, mood, lighting, camera angle, setting.
Make it artistic but keep commercial value.

Rules:
- Generate EXACTLY ${amount} prompt(s), no more no less
- Separate prompts with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot"

Example input with amount=2: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot | Elegant dog savoring premium kibble, modern kitchen setting, warm backlighting, low angle perspective"

Do not explain or add any other text. Just return the prompts separated by |.`;
      }

      const result = await model.generateContent([
        systemPrompt,
        keyword
      ]);
      
      const response = await result.response;
      const text = response.text();
      
      // คำนวณค่าใช้จ่าย (ประมาณการเนื่องจาก Gemini API อาจไม่ส่งค่า token usage มา)
      const usage = {
        input_tokens: (systemPrompt.length + keyword.length) / 4, // ประมาณการ
        output_tokens: text.length / 4 // ประมาณการ
      };
      const cost = calculateCost(usage);

      return {
        content: text,
        cost
      };
    } catch (err) {
      console.error('Error generating prompt with Gemini Free:', err);
      
      // ตรวจสอบประเภทของข้อผิดพลาดเพื่อแสดงข้อความที่ชัดเจน
      if (err.message.includes('API key')) {
        throw new Error('API key ของ Gemini ไม่ถูกต้อง กรุณาตรวจสอบและแก้ไขในหน้าโปรไฟล์');
      } else if (err.message.includes('network') || err.message.includes('Network')) {
        throw new Error('เกิดปัญหาการเชื่อมต่อกับ Gemini API กรุณาลองใหม่อีกครั้ง');
      } else if (err.message.includes('quota') || err.message.includes('limit')) {
        throw new Error('คุณใช้ Gemini API เกินโควต้าที่กำหนด กรุณาตรวจสอบการใช้งานหรือเปลี่ยน API key');
      } else {
        throw new Error('ไม่สามารถสร้าง prompt ด้วย Gemini Free ได้: ' + err.message);
      }
    }
  };
  
  const value = {
    gemini,
    model,
    loading,
    error,
    generatePrompt,
    generatePromptFree,
    requestCount,
    isRateLimited,
    initializeGemini
  };

  return (
    <GeminiContext.Provider value={value}>
      {children}
    </GeminiContext.Provider>
  );
};

export const useGemini = () => {
  const context = useContext(GeminiContext);
  if (!context) {
    throw new Error('useGemini must be used within a GeminiProvider');
  }
  return context;
};
